import { useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import OtpVerifyForm from './OtpVerifyForm';
import ForgotPasswordFlow from './ForgotPasswordFlow';
import { BootScreen } from './Logo';
import PasswordInput from './PasswordInput';

const MIN_POST_LOGIN_MS = 2000;

export default function LoginPage({ onSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [welcoming, setWelcoming] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login, completeTwoFactorLogin } = useContext(AuthContext);
  const emailRef = useRef(null);

  // Reveals the app at least MIN_POST_LOGIN_MS after `startedAt`, but no
  // sooner — so a slow connection just extends the wait instead of cutting it short.
  const revealAfterMinDelay = (startedAt, user) => {
    setWelcoming(true);
    const remaining = Math.max(0, MIN_POST_LOGIN_MS - (Date.now() - startedAt));
    setTimeout(() => onSuccess(user), remaining);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const startedAt = Date.now();

    try {
      const result = await login(email, password);
      if (result?.requiresTwoFactor) {
        setIsLoading(false);
        setNeedsTwoFactor(true);
        return;
      }
      setIsLoading(false);
      revealAfterMinDelay(startedAt, result);
    } catch (err) {
      setIsLoading(false);
      if (err.requiresVerification) {
        setNeedsVerification(true);
      } else {
        setError(err.message);
        emailRef.current?.focus();
      }
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const startedAt = Date.now();
    try {
      const loggedInUser = await completeTwoFactorLogin(email, password, twoFactorCode.trim());
      setIsLoading(false);
      revealAfterMinDelay(startedAt, loggedInUser);
    } catch (err) {
      setIsLoading(false);
      setError(err.message);
    }
  };

  if (welcoming) {
    return <BootScreen message={t('auth.signingYouIn')} />;
  }

  if (needsVerification) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('auth.verifyYourEmail')}</h1>
          <OtpVerifyForm email={email} onVerified={(user) => revealAfterMinDelay(Date.now(), user)} onBack={() => setNeedsVerification(false)} />
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <ForgotPasswordFlow
        onSuccess={(user) => revealAfterMinDelay(Date.now(), user)}
        onBack={() => setShowForgotPassword(false)}
      />
    );
  }

  if (needsTwoFactor) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{t('auth.twoFactorTitle')}</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: -8 }}>
            {t('auth.twoFactorPrompt')}
          </p>
          <form onSubmit={handleTwoFactorSubmit} className="auth-form">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
              style={{ letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.2rem' }}
            />
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" disabled={isLoading || twoFactorCode.length < 6} className="btn submit">
              {isLoading && <span className="btn-spinner" />}
              {isLoading ? t('auth.signingIn') : t('auth.verify')}
            </button>
            <button type="button" className="auth-forgot-link" onClick={() => { setNeedsTwoFactor(false); setTwoFactorCode(''); setError(''); }}>
              {t('auth.back')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>{t('auth.signIn')}</h1>
        <form onSubmit={handleSubmit} className="auth-form">
          <input
            ref={emailRef}
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <PasswordInput
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="button" className="auth-forgot-link" onClick={() => setShowForgotPassword(true)}>
            {t('auth.forgotPassword')}
          </button>
          {error && <div className="auth-error">{error}</div>}
          <button type="submit" disabled={isLoading} className="btn submit">
            {isLoading && <span className="btn-spinner" />}
            {isLoading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>
      </div>
    </div>
  );
}
